import { Optional, Inject, InjectionToken } from '@angular/core'
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core'
import dayJS, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import localeData from 'dayjs/plugin/localeData'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'

export interface DayJsDateAdapterOptions {
  /**
   * Turns the use of utc dates on or off.
   * Changing this will change how Angular Material components like DatePicker output dates.
   * {@default false}
   */
  useUtc?: boolean
}

/** InjectionToken for Dayjs date adapter to configure options. */
export const MAT_DAYJS_DATE_ADAPTER_OPTIONS =
  new InjectionToken<DayJsDateAdapterOptions>(
    'MAT_DAYJS_DATE_ADAPTER_OPTIONS',
    {
      providedIn: 'root',
      factory: MAT_DAYJS_DATE_ADAPTER_OPTIONS_FACTORY,
    }
  )

export function MAT_DAYJS_DATE_ADAPTER_OPTIONS_FACTORY(): DayJsDateAdapterOptions {
  return {
    useUtc: false,
  }
}

/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array(length)
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i)
  }
  return valuesArray
}

/** Adapts Dayjs Dates for use with Angular Material. */
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
  private localeData: {
    firstDayOfWeek: number
    longMonths: string[]
    shortMonths: string[]
    dates: string[]
    longDaysOfWeek: string[]
    shortDaysOfWeek: string[]
    narrowDaysOfWeek: string[]
  }

  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) public dateLocale: string,
    @Optional()
    @Inject(MAT_DAYJS_DATE_ADAPTER_OPTIONS)
    private options?: DayJsDateAdapterOptions
  ) {
    super()

    this.initializeParser(dateLocale)
  }

  private get shouldUseUtc(): boolean {
    const { useUtc }: DayJsDateAdapterOptions = this.options || {}
    return !!useUtc
  }

  /**
   * Set locale
   * @param locale
   */
  public override setLocale(locale: string) {
    super.setLocale(locale)

    const dayJsLocaleData = this.dayJs().localeData()
    this.localeData = {
      firstDayOfWeek: dayJsLocaleData.firstDayOfWeek(),
      longMonths: dayJsLocaleData.months(),
      shortMonths: dayJsLocaleData.monthsShort(),
      dates: range(31, (i) => this.createDate(2017, 0, i + 1).format('D')),
      longDaysOfWeek: range(7, (i) =>
        this.dayJs().set('day', i).format('dddd')
      ),
      shortDaysOfWeek: dayJsLocaleData.weekdaysShort(),
      narrowDaysOfWeek: dayJsLocaleData.weekdaysMin(),
    }
  }

  /**
   * Get year
   * @param date
   * @returns
   */
  public getYear(date: Dayjs): number {
    return this.dayJs(date).year()
  }

  /**
   * Get month
   * @param date
   * @returns
   */
  public getMonth(date: Dayjs): number {
    return this.dayJs(date).month()
  }

  /**
   * Get date
   * @param date
   * @returns
   */
  public getDate(date: Dayjs): number {
    return this.dayJs(date).date()
  }

  /**
   * Get day of week
   * @param date
   * @returns
   */
  public getDayOfWeek(date: Dayjs): number {
    return this.dayJs(date).day()
  }

  /**
   * Get month names
   * @param style
   * @returns
   */
  public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return style === 'long'
      ? this.localeData.longMonths
      : this.localeData.shortMonths
  }

  /**
   * Get date names
   * @returns
   */
  public getDateNames(): string[] {
    return this.localeData.dates
  }

  /**
   * Get day of week names
   * @param style
   * @returns
   */
  public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') {
      return this.localeData.longDaysOfWeek
    }
    if (style === 'short') {
      return this.localeData.shortDaysOfWeek
    }
    return this.localeData.narrowDaysOfWeek
  }

  /**
   * Get year name
   * @param date
   * @returns
   */
  public getYearName(date: Dayjs): string {
    return this.dayJs(date).format('YYYY')
  }

  /**
   * Get first day of week
   * @returns
   */
  public getFirstDayOfWeek(): number {
    return this.localeData.firstDayOfWeek
  }

  /**
   * Get num days in month
   * @param date
   * @returns
   */
  public getNumDaysInMonth(date: Dayjs): number {
    return this.dayJs(date).daysInMonth()
  }

  public clone(date: Dayjs): Dayjs {
    return date.clone()
  }

  /**
   * Create date
   * @param year
   * @param month
   * @param date
   * @returns
   */
  public createDate(year: number, month: number, date: number): Dayjs {
    const returnDayjs = this.dayJs()
      .set('year', year)
      .set('month', month)
      .set('date', date)
    return returnDayjs
  }

  /**
   * Get today
   * @returns
   */
  public today(): Dayjs {
    return this.dayJs()
  }

  /**
   * Get first day of week
   * @param value
   * @param parseFormat
   * @returns
   */
  public parse(value: any, parseFormat: string): Dayjs | null {
    if (value && typeof value === 'string') {
      return this.dayJs(
        value,
        dayJS().localeData().longDateFormat(parseFormat),
        this.locale
      )
    }
    return value ? this.dayJs(value).locale(this.locale) : null
  }

  /**
   * Format
   * @param date
   * @param displayFormat
   * @returns
   */
  public format(date: Dayjs, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw Error('DayjsDateAdapter: Cannot format invalid date.')
    }
    return date.locale(this.locale).format(displayFormat)
  }

  /**
   * Add calendar years
   * @param date
   * @param years
   * @returns
   */
  public addCalendarYears(date: Dayjs, years: number): Dayjs {
    return date.add(years, 'year')
  }

  /**
   * Add calendar months
   * @param date
   * @param months
   * @returns
   */
  public addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return date.add(months, 'month')
  }

  /**
   * Add calendar days
   * @param date
   * @param days
   * @returns
   */
  public addCalendarDays(date: Dayjs, days: number): Dayjs {
    return date.add(days, 'day')
  }

  /**
   * Date to iso 8601
   * @param date
   * @returns
   */
  public toIso8601(date: Dayjs): string {
    return date.toISOString()
  }

  /**
   * Attempts to deserialize a value to a valid date object. This is different from parsing in that
   * deserialize should only accept non-ambiguous, locale-independent formats (e.g. a ISO 8601
   * string). The default implementation does not allow any deserialization, it simply checks that
   * the given value is already a valid date object or null. The `<mat-datepicker>` will call this
   * method on all of it's `@Input()` properties that accept dates. It is therefore possible to
   * support passing values from your backend directly to these properties by overriding this method
   * to also deserialize the format used by your backend.
   * @param value The value to be deserialized into a date object.
   * @returns The deserialized date object, either a valid date, null if the value can be
   *     deserialized into a null date (e.g. the empty string), or an invalid date.
   */
  public override deserialize(value: any): Dayjs | null {
    let date
    if (value instanceof Date) {
      date = this.dayJs(value)
    } else if (this.isDateInstance(value)) {
      // NOTE: assumes that cloning also sets the correct locale.
      return this.clone(value)
    }
    if (typeof value === 'string') {
      if (!value) {
        return null
      }
      date = this.dayJs(value).toISOString()
    }
    if (date && this.isValid(date)) {
      return this.dayJs(date) // NOTE: Is this necessary since Dayjs is immutable and Moment was not?
    }
    return super.deserialize(value)
  }

  /**
   * Is date instance
   * @param obj
   * @returns
   */
  public isDateInstance(obj: any): boolean {
    return dayJS.isDayjs(obj)
  }

  /**
   * Is valid
   * @param date
   * @returns
   */
  public isValid(date: Dayjs): boolean {
    return this.dayJs(date).isValid()
  }

  /**
   * Invalid date
   * @returns
   */
  public invalid(): Dayjs {
    return this.dayJs(null)
  }

  /**
   * dayJs date
   * @param input
   * @param format
   * @param locale
   * @returns
   */
  private dayJs(input?: any, format?: string, locale?: string): Dayjs {
    if (!this.shouldUseUtc) {
      return dayJS(input, { format, locale }, locale)
    }
    return dayJS(
      input,
      { format, locale, utc: this.shouldUseUtc },
      locale
    ).utc()
  }

  /**
   * Initialize parser
   * @param dateLocale
   */
  private initializeParser(dateLocale: string) {
    if (this.shouldUseUtc) {
      dayJS.extend(utc)
    }

    dayJS.extend(LocalizedFormat)
    dayJS.extend(customParseFormat)
    dayJS.extend(localeData)

    this.setLocale(dateLocale)
  }
}
